if (!customElements.get('product-buy-row-form')) {
  customElements.define(
    'product-buy-row-form',
    class ProductBuyRowForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[type="submit"][name="add"]');
        this.submitButtonText = this.submitButton?.querySelector('.product-buy-row__atc-text');
        this.buyNowButton = this.querySelector('[data-buy-now]');
        this.hideErrors = this.dataset.hideErrors === 'true';
        this.addedFromThisForm = false;
        this.syncingCart = false;
      }

      connectedCallback() {
        if (!this.form) return;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.buyNowButton?.addEventListener('click', this.onBuyNowHandler.bind(this));

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data?.sectionId !== this.dataset.sectionId) return;
          const remoteBtn = event.data.html.getElementById(`ProductSubmitButton-${this.dataset.sectionId}`);
          if (!this.submitButton || !remoteBtn) return;

          const disabled = remoteBtn.hasAttribute('disabled');
          this.submitButton.toggleAttribute('disabled', disabled);
          this.buyNowButton?.toggleAttribute('disabled', disabled);

          const remoteText = remoteBtn.querySelector('span');
          const localText = this.submitButton.querySelector('.product-buy-row__atc-text');
          if (remoteText && localText && remoteText.textContent.trim()) {
            localText.textContent = remoteText.textContent.trim();
          }

          this.syncFromCart();
        });

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          this.syncFromCart(event?.cartData);
        });

        if (this.isInCartState()) {
          this.setInCartState(true, { animate: false });
        } else {
          this.syncFromCart();
        }
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
      }

      getVariantId() {
        return this.form?.querySelector('[name="id"]')?.value || '';
      }

      isInCartState() {
        return Boolean(this.submitButton?.classList.contains('pbr-in-cart'));
      }

      isFullCartPayload(cartPayload) {
        return (
          cartPayload &&
          Array.isArray(cartPayload.items) &&
          typeof cartPayload.item_count === 'number'
        );
      }

      variantInCart(cart, variantId) {
        if (!cart?.items || !variantId) return false;
        const id = String(variantId);
        return cart.items.some((item) => {
          const itemId = String(item.variant_id || item.id);
          return itemId === id && (item.quantity || 0) > 0;
        });
      }

      syncFromCart(cartPayload) {
        const variantId = this.getVariantId();
        if (!variantId || this.syncingCart) return;

        const applyCart = (cart) => {
          const inCart = this.variantInCart(cart, variantId);
          this.setInCartState(inCart, { animate: false });
          this.syncingCart = false;
        };

        if (this.isFullCartPayload(cartPayload)) {
          applyCart(cartPayload);
          return;
        }

        this.syncingCart = true;
        fetch(`${routes.cart_url}.js`)
          .then((response) => response.json())
          .then(applyCart)
          .catch(() => {
            this.syncingCart = false;
          });
      }

      setInCartState(inCart, { animate = false } = {}) {
        if (!this.submitButton) return;

        const currentlyInCart = this.isInCartState();
        if (inCart && currentlyInCart) {
          this.addedFromThisForm = true;
          this.submitButton.setAttribute('aria-disabled', 'true');
          return;
        }

        clearTimeout(this.successTimer);

        if (inCart) {
          this.addedFromThisForm = true;
          this.submitButton.classList.add('pbr-success', 'pbr-in-cart');
          if (animate) {
            this.submitButton.classList.remove('pbr-success-static');
            // Retrigger success animation cleanly on a fresh add.
            void this.submitButton.offsetWidth;
          } else {
            this.submitButton.classList.add('pbr-success-static');
          }
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButton.setAttribute('aria-label', 'Already in cart');
        } else {
          this.addedFromThisForm = false;
          this.submitButton.classList.remove('pbr-success', 'pbr-in-cart', 'pbr-success-static');
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.removeAttribute('aria-label');

          const variantInput = this.form?.querySelector('[name="id"]');
          if (variantInput?.disabled) {
            this.submitButton.disabled = true;
          }
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.isInCartState()) return;
        if (this.submitButton?.getAttribute('aria-disabled') === 'true') return;
        if (this.submitButton?.disabled) return;

        this.handleErrorMessage();
        this.showSuccess();
        this.setLoading(true);

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        formData.append('properties[_add_source]', 'Main Add to Cart');
        formData.append('sections', this.getSectionsToFetch().join(','));
        formData.append('sections_url', window.location.pathname);
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.handleErrorMessage(response.description);
              this.error = true;
              this.hideSuccess();
              return;
            }

            this.error = false;
            this.setInCartState(true, { animate: true });
            this.openCartDrawer(response);

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-buy-row-form',
              productVariantId: formData.get('id'),
              cartData: response,
            });
          })
          .catch((e) => {
            this.hideSuccess();
            console.error(e);
          })
          .finally(() => {
            this.setLoading(false);
            if (!this.error && !this.isInCartState()) {
              this.submitButton?.removeAttribute('aria-disabled');
            }
          });
      }

      onBuyNowHandler() {
        if (this.buyNowButton?.disabled) return;

        // If this product was already added from this form, checkout the
        // current cart as-is so Buy now does not add a duplicate.
        if (this.addedFromThisForm || this.isInCartState()) {
          window.location.href =
            typeof window.bundleUpsellCheckoutUrl === 'function'
              ? window.bundleUpsellCheckoutUrl()
              : '/checkout';
          return;
        }

        this.buyNowButton.classList.add('loading');
        this.setLoading(true, this.buyNowButton);

        const formData = new FormData(this.form);
        const config = fetchConfig('json');
        config.method = 'POST';
        config.body = JSON.stringify({
          items: [
            {
              id: formData.get('id'),
              quantity: parseInt(formData.get('quantity') || '1', 10),
              properties: {
                _add_source: 'Main Buy Now',
              },
            },
          ],
        });

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.handleErrorMessage(response.description);
              return;
            }
            window.location.href =
              typeof window.bundleUpsellCheckoutUrl === 'function'
                ? window.bundleUpsellCheckoutUrl()
                : '/checkout';
          })
          .catch((e) => console.error(e))
          .finally(() => {
            this.setLoading(false, this.buyNowButton);
            this.buyNowButton?.classList.remove('loading');
          });
      }

      getSectionsToFetch() {
        return ['cart-icon-bubble', 'cart-drawer'];
      }

      updateCartSections(parsedState) {
        if (!parsedState.sections) return;

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer?.getSectionsToRender) {
          cartDrawer.getSectionsToRender().forEach((section) => {
            const sectionHtml = parsedState.sections[section.id];
            if (!sectionHtml) return;

            const sectionElement = section.selector
              ? document.querySelector(section.selector)
              : document.getElementById(section.id);

            if (!sectionElement) return;
            sectionElement.innerHTML = cartDrawer.getSectionInnerHTML(sectionHtml, section.selector);
          });
          cartDrawer.classList.remove('is-empty');
          const overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
          overlay?.addEventListener('click', cartDrawer.close.bind(cartDrawer));
        }

        const bubbleSection = document.getElementById('shopify-section-cart-icon-bubble');
        if (bubbleSection && parsedState.sections['cart-icon-bubble']) {
          const html = new DOMParser().parseFromString(parsedState.sections['cart-icon-bubble'], 'text/html');
          const inner = html.querySelector('.shopify-section');
          if (inner) bubbleSection.innerHTML = inner.innerHTML;
        }

        cartDrawer?.setHeaderCartIconAccessibility?.();
        this.animateCartCount();
      }

      openCartDrawer(parsedState) {
        const cartDrawer = document.querySelector('cart-drawer');
        if (!cartDrawer) {
          this.updateCartSections(parsedState);
          return;
        }
        if (parsedState?.sections && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(parsedState);
          return;
        }
        this.updateCartSections(parsedState);
        if (window.themeSettings?.cartDrawerAutoOpen) cartDrawer.open?.();
      }

      showSuccess() {
        if (!this.submitButton) return;
        clearTimeout(this.successTimer);
        this.submitButton.classList.remove('pbr-success-static');
        this.submitButton.classList.add('pbr-success');
      }

      hideSuccess(delay = 0) {
        if (this.isInCartState()) return;
        clearTimeout(this.successTimer);
        this.successTimer = setTimeout(() => {
          if (this.isInCartState()) return;
          this.submitButton?.classList.remove('pbr-success', 'pbr-success-static');
        }, delay);
      }

      animateCartCount() {
        const countBadge = document.querySelector('#cart-icon-bubble .cart-count-bubble');
        if (!countBadge) return;
        countBadge.classList.add('pbr-count-pop');
        setTimeout(() => {
          countBadge.classList.remove('pbr-count-pop');
        }, 450);
      }

      setLoading(isLoading, button = this.submitButton) {
        if (!button) return;
        button.classList.toggle('loading', isLoading);
        const spinner = button.querySelector('.loading__spinner');
        spinner?.classList.toggle('hidden', !isLoading);
        if (isLoading) {
          button.setAttribute('aria-disabled', 'true');
        }
      }

      handleErrorMessage(errorMessage = false) {
        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
        if (errorMessage) this.errorMessage.textContent = errorMessage;
      }
    }
  );
}
